/**
 * V2 Feedback Form Renderer
 * Implements category-aware progressive disclosure of structured feedback fields.
 * - Bugs: expectedBehavior, stepsToReproduce
 * - Improvements: successCriteria (desired outcome)
 * - Questions: contextualInfo (background/environment context)
 */

import type {
  FeedbackCategory,
  FeedbackSeverity,
  FeedbackPayload,
  CaptureType,
} from './types';
import { DiagnosticsCapture } from './diagnostics';
import { escapeHtml } from './dom-utils';
import {
  buildCategoryOptionsHtml,
  FEEDBACK_CATEGORIES,
  isFeedbackCategory,
} from './feedback-categories';
import { buildSeverityOptionsHtml } from './feedback-severity';

export interface V2FormState {
  category: FeedbackCategory;
  severity: FeedbackSeverity;
  title: string;
  description: string;
  expectedBehavior?: string;
  stepsToReproduce?: string;
  successCriteria?: string;
  contextualInfo?: string;
  testerEmail?: string;
}

/**
 * Build and render the v2 form with category-aware fields.
 * Returns form element and state manager.
 */
export function buildV2FormModal(options: {
  capturedType: CaptureType;
  storedEmail: string | null;
  emailCaptureEnabled: boolean;
  availableCategories?: FeedbackCategory[];
  onSubmit: (formData: HTMLFormElement) => Promise<void>;
}): {
  form: HTMLFormElement;
  getState: () => V2FormState;
  showFieldValidationError?: (field: string, message: string) => void;
} {
  const form = document.createElement('form');
  form.className = 'fh-v2-form';
  const availableCategories =
    options.availableCategories && options.availableCategories.length > 0
      ? options.availableCategories
      : [...FEEDBACK_CATEGORIES];
  const defaultCategory = availableCategories[0] ?? 'bug';

  // ─── Category + Severity row ────────────────────────────────────────
  const row1 = document.createElement('div');
  row1.className = 'fh-field-row';

  const categoryField = document.createElement('div');
  categoryField.className = 'fh-field';
  categoryField.innerHTML = `
    <label>Category</label>
    <select name="category" class="fh-v2-category-select" required>
      ${buildCategoryOptionsHtml(availableCategories, 'detailed')}
    </select>
  `;

  const severityField = document.createElement('div');
  severityField.className = 'fh-field';
  severityField.innerHTML = `
    <label>Severity</label>
    <select name="severity" required>
      ${buildSeverityOptionsHtml()}
    </select>
  `;

  row1.appendChild(categoryField);
  row1.appendChild(severityField);
  form.appendChild(row1);

  // ─── Title ─────────────────────────────────────────────────────────
  const titleField = document.createElement('div');
  titleField.className = 'fh-field';
  titleField.innerHTML = `
    <label>Title</label>
    <input type="text" name="title" placeholder="Brief summary" required maxlength="200" />
    <div class="fh-field-hint" style="font-size:12px;color:var(--fh-text-secondary);margin-top:4px"></div>
  `;
  form.appendChild(titleField);

  // ─── Description ────────────────────────────────────────────────────
  const descField = document.createElement('div');
  descField.className = 'fh-field';
  descField.innerHTML = `
    <label>Description</label>
    <textarea name="description" placeholder="What happened? What did you expect?" rows="3"></textarea>
    <div class="fh-field-hint" style="font-size:12px;color:var(--fh-text-secondary);margin-top:4px"></div>
  `;
  form.appendChild(descField);

  // ─── Category-specific structured fields ────────────────────────────

  // Bug fields
  const bugFieldsContainer = document.createElement('div');
  bugFieldsContainer.className = 'fh-bug-fields';
  bugFieldsContainer.style.display = 'none';

  const expectedBehaviorField = document.createElement('div');
  expectedBehaviorField.className = 'fh-field';
  expectedBehaviorField.innerHTML = `
    <label>Expected Behavior <span style="color:#999">(optional)</span></label>
    <textarea name="expectedBehavior" placeholder="What did you expect to happen?" rows="2"></textarea>
    <div class="fh-field-hint" style="font-size:12px;color:var(--fh-text-secondary);margin-top:4px">
      This helps the team understand what correct behavior looks like.
    </div>
  `;
  bugFieldsContainer.appendChild(expectedBehaviorField);

  const stepsToReproduceField = document.createElement('div');
  stepsToReproduceField.className = 'fh-field';
  stepsToReproduceField.innerHTML = `
    <label>Steps to Reproduce <span style="color:#999">(optional)</span></label>
    <textarea name="stepsToReproduce" placeholder="1. Click on...\n2. Then...\n3. Finally..." rows="3"></textarea>
    <div class="fh-field-hint" style="font-size:12px;color:var(--fh-text-secondary);margin-top:4px">
      Step-by-step instructions help us reproduce the issue quickly.
    </div>
  `;
  bugFieldsContainer.appendChild(stepsToReproduceField);
  form.appendChild(bugFieldsContainer);

  // Improvement fields
  const improvementFieldsContainer = document.createElement('div');
  improvementFieldsContainer.className = 'fh-improvement-fields';
  improvementFieldsContainer.style.display = 'none';

  const successCriteriaField = document.createElement('div');
  successCriteriaField.className = 'fh-field';
  successCriteriaField.innerHTML = `
    <label>What Success Looks Like <span style="color:#999">(optional)</span></label>
    <textarea name="successCriteria" placeholder="How would you know this improvement is working?" rows="2"></textarea>
    <div class="fh-field-hint" style="font-size:12px;color:var(--fh-text-secondary);margin-top:4px">
      Describe the desired outcome or behavior after this improvement is implemented.
    </div>
  `;
  improvementFieldsContainer.appendChild(successCriteriaField);
  form.appendChild(improvementFieldsContainer);

  // Question fields
  const questionFieldsContainer = document.createElement('div');
  questionFieldsContainer.className = 'fh-question-fields';
  questionFieldsContainer.style.display = 'none';

  const contextualInfoField = document.createElement('div');
  contextualInfoField.className = 'fh-field';
  contextualInfoField.innerHTML = `
    <label>Context <span style="color:#999">(optional)</span></label>
    <textarea name="contextualInfo" placeholder="Any background or environment details that might help?" rows="2"></textarea>
    <div class="fh-field-hint" style="font-size:12px;color:var(--fh-text-secondary);margin-top:4px">
      Share any relevant context like your workflow, use case, or environment.
    </div>
  `;
  questionFieldsContainer.appendChild(contextualInfoField);
  form.appendChild(questionFieldsContainer);

  // ─── Email field (optional) ─────────────────────────────────────────
  if (options.emailCaptureEnabled) {
    const emailField = document.createElement('div');
    emailField.className = 'fh-field';
    emailField.innerHTML = `
      <label>Email <span style="color:#999">(optional)</span></label>
      <input type="email" name="testerEmail" placeholder="you@example.com" value="${options.storedEmail ? escapeHtml(options.storedEmail) : ''}" />
      <div class="fh-field-hint" style="font-size:12px;color:var(--fh-text-secondary);margin-top:4px">
        So we can reply with updates about your feedback.
      </div>
    `;
    form.appendChild(emailField);
  }

  // ─── Submit button ──────────────────────────────────────────────────
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'fh-submit-btn';
  submitBtn.textContent = 'Submit Feedback';
  form.appendChild(submitBtn);

  // ─── Form behavior ──────────────────────────────────────────────────

  const categorySelect = form.querySelector(
    '.fh-v2-category-select',
  ) as HTMLSelectElement;
  const titleHint = form.querySelector('.fh-field-row + .fh-field .fh-field-hint') as HTMLDivElement;
  const descHint = form.querySelector(
    'textarea[name="description"]',
  )?.parentElement?.querySelector('.fh-field-hint') as HTMLDivElement;

  function updateFieldsForCategory(category: FeedbackCategory) {
    // Hide all containers
    (form.querySelector('.fh-bug-fields') as HTMLDivElement).style.display = 'none';
    (form.querySelector('.fh-improvement-fields') as HTMLDivElement).style.display = 'none';
    (form.querySelector('.fh-question-fields') as HTMLDivElement).style.display = 'none';

    // Update hints
    if (titleHint) {
      titleHint.textContent =
        category === 'bug'
          ? 'e.g., "Submit button not working on checkout page"'
          : category === 'improvement'
            ? 'e.g., "Dark mode support"'
            : 'e.g., "How do I export my data?"';
    }

    if (descHint) {
      descHint.textContent =
        category === 'bug'
          ? 'Include what went wrong and what you were trying to do.'
          : category === 'improvement'
            ? 'Explain why this would be valuable or what pain point it solves.'
            : 'Provide as much detail as possible so we can give you a great answer.';
    }

    // Show relevant container
    switch (category) {
      case 'bug':
        (form.querySelector('.fh-bug-fields') as HTMLDivElement).style.display = 'block';
        break;
      case 'improvement':
        (form.querySelector('.fh-improvement-fields') as HTMLDivElement).style.display = 'block';
        break;
      case 'question':
        (form.querySelector('.fh-question-fields') as HTMLDivElement).style.display = 'block';
        break;
    }
  }

  // Initialize and attach listener
  const initialCategory = isFeedbackCategory(categorySelect.value)
    ? categorySelect.value
    : defaultCategory;
  categorySelect.value = initialCategory;
  updateFieldsForCategory(initialCategory);

  categorySelect.addEventListener('change', (e) => {
    const selected = (e.target as HTMLSelectElement).value;
    updateFieldsForCategory(isFeedbackCategory(selected) ? selected : defaultCategory);
  });

  // ─── Form submission ────────────────────────────────────────────────
  form.onsubmit = (e) => {
    e.preventDefault();
    options.onSubmit(form);
  };

  // ─── State accessor ─────────────────────────────────────────────────
  function getState(): V2FormState {
    const data = new FormData(form);
    return {
      category: data.get('category') as FeedbackCategory,
      severity: data.get('severity') as FeedbackSeverity,
      title: data.get('title') as string,
      description: (data.get('description') as string) || '',
      expectedBehavior: ((data.get('expectedBehavior') as string) || '').trim() || undefined,
      stepsToReproduce: ((data.get('stepsToReproduce') as string) || '').trim() || undefined,
      successCriteria: ((data.get('successCriteria') as string) || '').trim() || undefined,
      contextualInfo: ((data.get('contextualInfo') as string) || '').trim() || undefined,
      testerEmail: ((data.get('testerEmail') as string) || '').trim() || undefined,
    };
  }

  return {
    form,
    getState,
  };
}
